import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Content from "@/components/specific/Content";
import { useDocumentTitle } from "@/utils/hooks/useDocumentTitle";
import { postRegistrasiPelanggan } from "@/utils/apis/public";

const JENIS_BANGUNAN_OPTIONS = [
  { value: "", label: "-- Pilih Jenis Bangunan --" },
  { value: "Permanen", label: "Permanen" },
  { value: "Semi Permanen", label: "Semi Permanen" },
  { value: "Non Permanen", label: "Non Permanen" },
];

const STATUS_BANGUNAN_OPTIONS = [
  { value: "", label: "-- Pilih Status Bangunan --" },
  { value: "Milik Sendiri", label: "Milik Sendiri" },
  { value: "Kontrak/Sewa", label: "Kontrak / Sewa" },
  { value: "Milik Orang Tua", label: "Milik Orang Tua" },
  { value: "Milik Keluarga", label: "Milik Keluarga" },
];

const INITIAL_FORM = {
  nama_pelanggan: "",
  email: "",
  alamat: "",
  handphone: "",
  nomor_ktp: "",
  id_kabupaten: "",
  id_kecamatan: "",
  id_kelurahan: "",
  telephone: "",
  rt: "",
  rw: "",
  latitude: "",
  longitude: "",
  jenis_bangunan: "",
  luas_tanah: "",
  luas_rumah: "",
  status_bangunan: "",
  sumber_air: "",
  pekerjaan: "",
  id_peruntukan_bangunan: "",
  foto_ktp: null,
};

const REQUIRED_FIELDS = {
  nama_pelanggan: "Nama pelanggan",
  email: "Email",
  alamat: "Alamat",
  handphone: "Nomor handphone",
  nomor_ktp: "Nomor KTP",
  id_kabupaten: "ID Kabupaten",
  id_kecamatan: "ID Kecamatan",
  id_kelurahan: "ID Kelurahan",
  rt: "RT",
  rw: "RW",
  latitude: "Latitude",
  longitude: "Longitude",
  jenis_bangunan: "Jenis bangunan",
  status_bangunan: "Status bangunan",
};

function FieldError({ message }) {
  if (!message) return null;
  return <p className="field-error text-xs text-red-500 mt-1">{message}</p>;
}

function SectionTitle({ icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-100">
      <div className="bg-[#095393]/10 p-2 rounded-lg text-[#095393]">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-800">{title}</h3>
    </div>
  );
}

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      <FieldError message={error} />
    </div>
  );
}

const inputClass = (error) =>
  `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#095393] focus:border-transparent transition-colors ${
    error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

const selectClass = (error) =>
  `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#095393] focus:border-transparent transition-colors appearance-none bg-white ${
    error ? "border-red-400 bg-red-50" : "border-gray-300"
  }`;

function RegistrasiPelanggan() {
  useDocumentTitle("Registrasi Pelanggan - Perumda Air Minum Cilegon Mandiri");

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        foto_ktp: "File harus berupa gambar (JPG, PNG, dll)",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, foto_ktp: "Ukuran file maksimal 5 MB" }));
      return;
    }
    setForm((prev) => ({ ...prev, foto_ktp: file }));
    setPhotoPreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, foto_ktp: "" }));
  };

  const removePhoto = () => {
    setForm((prev) => ({ ...prev, foto_ktp: null }));
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation tidak didukung oleh browser Anda.");
      return;
    }
    setIsGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }));
        setErrors((prev) => ({ ...prev, latitude: "", longitude: "" }));
        setIsGeoLoading(false);
      },
      () => {
        alert("Gagal mendeteksi lokasi. Masukkan koordinat secara manual.");
        setIsGeoLoading(false);
      },
    );
  };

  const validate = () => {
    const newErrors = {};

    Object.entries(REQUIRED_FIELDS).forEach(([key, label]) => {
      if (!form[key] || String(form[key]).trim() === "") {
        newErrors[key] = `${label} wajib diisi`;
      }
    });

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Format email tidak valid";
    }

    if (form.nomor_ktp && !/^\d{16}$/.test(form.nomor_ktp)) {
      newErrors.nomor_ktp = "Nomor KTP harus 16 digit angka";
    }

    if (form.handphone && !/^\d{8,15}$/.test(form.handphone)) {
      newErrors.handphone = "Nomor handphone tidak valid (8-15 digit)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstErrorEl = document.querySelector(".field-error");
      if (firstErrorEl)
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      const response = await postRegistrasiPelanggan(formData);
      setSubmitStatus("success");
      setSubmitMessage(
        response?.data?.message ||
          "Registrasi berhasil! Kami akan menghubungi Anda segera.",
      );
      setForm(INITIAL_FORM);
      setPhotoPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitStatus("error");
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan. Silakan coba lagi.";
      setSubmitMessage(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <div
        className="relative bg-[#095393] overflow-hidden w-screen z-0"
        style={{
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          width: "100vw",
          maxWidth: "100vw",
        }}
      >
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-16 md:h-24"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Registrasi Pelanggan
            </h1>
            <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
              <span className="text-white/60">/</span>
              <span className="text-white">Registrasi Pelanggan</span>
            </div>
          </div>
        </div>
      </div>

      <Content showContentHeader={false}>
        <div className="w-full max-w-3xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 relative z-20 pb-12">
          {/* Alert Status */}
          {submitStatus === "success" && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-sm">Registrasi Berhasil!</p>
                <p className="text-sm mt-0.5">{submitMessage}</p>
              </div>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-sm">Registrasi Gagal</p>
                <p className="text-sm mt-0.5">{submitMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* ── Seksi 1: Data Diri ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              <SectionTitle
                title="Data Diri"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField
                    label="Nama Pelanggan"
                    required
                    error={errors.nama_pelanggan}
                  >
                    <input
                      type="text"
                      name="nama_pelanggan"
                      value={form.nama_pelanggan}
                      onChange={handleChange}
                      placeholder="Masukkan nama lengkap"
                      className={inputClass(errors.nama_pelanggan)}
                    />
                  </FormField>
                </div>

                <FormField label="Email" required error={errors.email}>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="contoh@email.com"
                    className={inputClass(errors.email)}
                  />
                </FormField>

                <FormField
                  label="Nomor Handphone"
                  required
                  error={errors.handphone}
                >
                  <input
                    type="tel"
                    name="handphone"
                    value={form.handphone}
                    onChange={handleChange}
                    placeholder="08xxxxxxxxxx"
                    className={inputClass(errors.handphone)}
                  />
                </FormField>

                <FormField
                  label="Nomor Telephone (opsional)"
                  error={errors.telephone}
                >
                  <input
                    type="tel"
                    name="telephone"
                    value={form.telephone}
                    onChange={handleChange}
                    placeholder="021xxxxxxx"
                    className={inputClass(errors.telephone)}
                  />
                </FormField>

                <FormField label="Nomor KTP" required error={errors.nomor_ktp}>
                  <input
                    type="text"
                    name="nomor_ktp"
                    value={form.nomor_ktp}
                    onChange={handleChange}
                    placeholder="16 digit nomor KTP"
                    maxLength={16}
                    className={inputClass(errors.nomor_ktp)}
                  />
                </FormField>

                <FormField
                  label="Pekerjaan (opsional)"
                  error={errors.pekerjaan}
                >
                  <input
                    type="text"
                    name="pekerjaan"
                    value={form.pekerjaan}
                    onChange={handleChange}
                    placeholder="Masukkan pekerjaan"
                    className={inputClass(errors.pekerjaan)}
                  />
                </FormField>
              </div>
            </div>

            {/* ── Seksi 2: Alamat & Lokasi ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              <SectionTitle
                title="Alamat & Lokasi"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField
                    label="Alamat Lengkap"
                    required
                    error={errors.alamat}
                  >
                    <textarea
                      name="alamat"
                      value={form.alamat}
                      onChange={handleChange}
                      placeholder="Masukkan alamat lengkap"
                      rows={3}
                      className={`${inputClass(errors.alamat)} resize-none`}
                    />
                  </FormField>
                </div>

                <FormField label="RT" required error={errors.rt}>
                  <input
                    type="text"
                    name="rt"
                    value={form.rt}
                    onChange={handleChange}
                    placeholder="Contoh: 001"
                    className={inputClass(errors.rt)}
                  />
                </FormField>

                <FormField label="RW" required error={errors.rw}>
                  <input
                    type="text"
                    name="rw"
                    value={form.rw}
                    onChange={handleChange}
                    placeholder="Contoh: 002"
                    className={inputClass(errors.rw)}
                  />
                </FormField>

                <FormField
                  label="ID Kabupaten"
                  required
                  error={errors.id_kabupaten}
                >
                  <input
                    type="text"
                    name="id_kabupaten"
                    value={form.id_kabupaten}
                    onChange={handleChange}
                    placeholder="Masukkan ID Kabupaten"
                    className={inputClass(errors.id_kabupaten)}
                  />
                </FormField>

                <FormField
                  label="ID Kecamatan"
                  required
                  error={errors.id_kecamatan}
                >
                  <input
                    type="text"
                    name="id_kecamatan"
                    value={form.id_kecamatan}
                    onChange={handleChange}
                    placeholder="Masukkan ID Kecamatan"
                    className={inputClass(errors.id_kecamatan)}
                  />
                </FormField>

                <div className="md:col-span-2">
                  <FormField
                    label="ID Kelurahan"
                    required
                    error={errors.id_kelurahan}
                  >
                    <input
                      type="text"
                      name="id_kelurahan"
                      value={form.id_kelurahan}
                      onChange={handleChange}
                      placeholder="Masukkan ID Kelurahan"
                      className={inputClass(errors.id_kelurahan)}
                    />
                  </FormField>
                </div>

                {/* Koordinat */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      Koordinat Lokasi <span className="text-red-500">*</span>
                    </span>
                    <button
                      type="button"
                      onClick={detectLocation}
                      disabled={isGeoLoading}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#095393] hover:text-[#074a7a] transition-colors disabled:opacity-50"
                    >
                      {isGeoLoading ? (
                        <>
                          <svg
                            className="animate-spin h-3.5 w-3.5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Mendeteksi...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Deteksi Otomatis
                        </>
                      )}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        name="latitude"
                        value={form.latitude}
                        onChange={handleChange}
                        placeholder="Latitude"
                        className={inputClass(errors.latitude)}
                      />
                      <FieldError message={errors.latitude} />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="longitude"
                        value={form.longitude}
                        onChange={handleChange}
                        placeholder="Longitude"
                        className={inputClass(errors.longitude)}
                      />
                      <FieldError message={errors.longitude} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Seksi 3: Informasi Bangunan ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              <SectionTitle
                title="Informasi Bangunan"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Jenis Bangunan"
                  required
                  error={errors.jenis_bangunan}
                >
                  <div className="relative">
                    <select
                      name="jenis_bangunan"
                      value={form.jenis_bangunan}
                      onChange={handleChange}
                      className={selectClass(errors.jenis_bangunan)}
                    >
                      {JENIS_BANGUNAN_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </FormField>

                <FormField
                  label="Status Bangunan"
                  required
                  error={errors.status_bangunan}
                >
                  <div className="relative">
                    <select
                      name="status_bangunan"
                      value={form.status_bangunan}
                      onChange={handleChange}
                      className={selectClass(errors.status_bangunan)}
                    >
                      {STATUS_BANGUNAN_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </FormField>

                <FormField
                  label="Luas Tanah (m²) (opsional)"
                  error={errors.luas_tanah}
                >
                  <input
                    type="number"
                    name="luas_tanah"
                    value={form.luas_tanah}
                    onChange={handleChange}
                    placeholder="Contoh: 120"
                    min="0"
                    className={inputClass(errors.luas_tanah)}
                  />
                </FormField>

                <FormField
                  label="Luas Rumah (m²) (opsional)"
                  error={errors.luas_rumah}
                >
                  <input
                    type="number"
                    name="luas_rumah"
                    value={form.luas_rumah}
                    onChange={handleChange}
                    placeholder="Contoh: 60"
                    min="0"
                    className={inputClass(errors.luas_rumah)}
                  />
                </FormField>

                <FormField
                  label="Sumber Air Saat Ini (opsional)"
                  error={errors.sumber_air}
                >
                  <input
                    type="text"
                    name="sumber_air"
                    value={form.sumber_air}
                    onChange={handleChange}
                    placeholder="Contoh: Sumur, Air hujan"
                    className={inputClass(errors.sumber_air)}
                  />
                </FormField>

                <FormField
                  label="ID Peruntukan Bangunan (opsional)"
                  error={errors.id_peruntukan_bangunan}
                >
                  <input
                    type="text"
                    name="id_peruntukan_bangunan"
                    value={form.id_peruntukan_bangunan}
                    onChange={handleChange}
                    placeholder="Masukkan ID peruntukan"
                    className={inputClass(errors.id_peruntukan_bangunan)}
                  />
                </FormField>
              </div>
            </div>

            {/* ── Seksi 4: Dokumen ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <SectionTitle
                title="Dokumen"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                }
              />

              <FormField label="Foto KTP (opsional)" error={errors.foto_ktp}>
                {!photoPreview ? (
                  <label className="flex flex-col items-center justify-center w-full py-10 px-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-10 w-10 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm text-gray-500 text-center">
                        <span className="font-medium text-[#095393]">
                          Klik untuk upload
                        </span>{" "}
                        atau drag & drop
                      </p>
                      <p className="text-xs text-gray-400">
                        JPG, PNG, WEBP — Maks. 5 MB
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="relative inline-block">
                    <img
                      src={photoPreview}
                      alt="Preview KTP"
                      className="w-full max-w-sm h-48 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </FormField>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="flex-1 text-center py-3 px-6 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors no-underline"
              >
                Batal
              </Link>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-[#095393] hover:bg-[#074a7a] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Mendaftarkan...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Daftar Sekarang
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Content>
    </>
  );
}

export default RegistrasiPelanggan;
